import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Check, AlertCircle } from "lucide-react";

interface ZipCodeInputProps {
  value: string;
  onChange: (zipCode: string, city: string, state: string, lat: number, lon: number) => void;
  city?: string;
  state?: string;
  disabled?: boolean;
}

interface ZippopotamusResponse {
  'post code': string;
  country: string;
  'country abbreviation': string;
  places: Array<{
    'place name': string;
    longitude: string;
    state: string;
    'state abbreviation': string;
    latitude: string;
  }>;
}

export const ZipCodeInput = ({ value, onChange, city, state, disabled }: ZipCodeInputProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(!!city && !!state);

  const lookupZipCode = async (zipCode: string) => {
    if (!/^\d{5}$/.test(zipCode)) {
      setError("Please enter a valid 5-digit zip code");
      setIsValid(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          setError("Zip code not found");
        } else {
          setError("Failed to lookup zip code");
        }
        setIsValid(false);
        return;
      }

      const data: ZippopotamusResponse = await response.json();
      
      if (data.places && data.places.length > 0) {
        const place = data.places[0];
        onChange(
          zipCode,
          place['place name'],
          place['state abbreviation'],
          parseFloat(place.latitude),
          parseFloat(place.longitude)
        );
        setIsValid(true);
        setError(null);
      } else {
        setError("No location data found");
        setIsValid(false);
      }
    } catch (err) {
      console.error('Zip code lookup error:', err);
      setError("Failed to lookup zip code");
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 5);
    setInputValue(newValue);
    setIsValid(false);
    setError(null);
  };

  const handleBlur = () => {
    if (inputValue.length === 5 && inputValue !== value) {
      lookupZipCode(inputValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.length === 5) {
      lookupZipCode(inputValue);
    }
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="zipCode">Home Zip Code</Label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="zipCode"
            placeholder="Enter 5-digit zip code"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className="pl-9"
            maxLength={5}
            disabled={disabled || loading}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => lookupZipCode(inputValue)}
          disabled={disabled || loading || inputValue.length !== 5}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isValid ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <MapPin className="h-4 w-4" />
          )}
        </Button>
      </div>
      
      {error && (
        <div className="flex items-center gap-1 text-sm text-destructive">
          <AlertCircle className="h-3 w-3" />
          <span>{error}</span>
        </div>
      )}
      
      {isValid && city && state && (
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          <Check className="h-3 w-3 text-green-600" />
          {city}, {state}
        </p>
      )}
    </div>
  );
};
